import * as React from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, Lock } from "lucide-react";
import { preLoginWithEmail2fa, ApiError } from "@/lib/auth-api";
import { useLanguage } from "@/contexts/LanguageContext";

interface ExistingUserFormProps {
  email: string;
  onBack: () => void;
  onTwoFactor: (password: string) => void;
}

export const ExistingUserForm: React.FC<ExistingUserFormProps> = ({ email, onBack, onTwoFactor }) => {
  const [password, setPassword] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const passwordRef = React.useRef<HTMLInputElement>(null);
  const { t } = useLanguage();

  React.useEffect(() => {
    passwordRef.current?.focus();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) return;

    setIsLoading(true);
    setError("");

    try {
      await preLoginWithEmail2fa(email, password);
      onTwoFactor(password);
    } catch (err) {
      if (err instanceof ApiError && err.response.status === 401) {
        setError(t("auth.error.invalidCredentials"));
      } else if (err instanceof ApiError && err.response.status === 403) {
        setError(t("auth.error.tooManyAttempts"));
      } else {
        setError(t("auth.error.generic"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 rounded-full hover:bg-secondary transition-colors">
          <ArrowLeft className="w-5 h-5 text-foreground" />
        </button>
        <h2 className="text-2xl font-bold text-foreground flex-1 text-center pe-10">
          {t("auth.welcomeBack.title")}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="text-sm font-medium text-foreground mb-1.5 block">{t("auth.email")}</label>
          <input
            type="email"
            value={email}
            disabled
            className="w-full px-4 py-3 rounded-lg border border-input bg-muted text-muted-foreground"
          />
        </div>

        <div>
          <label htmlFor="password" className="text-sm font-medium text-foreground mb-1.5 block">
            {t("auth.password")}
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              ref={passwordRef}
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("auth.passwordPlaceholder")}
              className="w-full pl-10 pr-4 py-3 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              required
            />
          </div>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <Button
          type="submit"
          disabled={isLoading || !password.trim()}
          className="w-full py-6 text-base font-semibold rounded-full bg-primary hover:bg-primary/90 text-primary-foreground"
        >
          {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : t("auth.signIn.cta")}
        </Button>
      </form>
    </div>
  );
};
